import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Send } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = "https://website-trivenza-2.onrender.com/api";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen pt-32">
      {/* Header */}
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-7xl font-outfit font-bold tracking-tighter text-white mb-6"
          >
            Contact Us
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-base md:text-lg text-gray-400 font-manrope max-w-3xl mx-auto"
          >
            Get in touch with us for business inquiries, client projects, or collaborations.
          </motion.p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12">
          {/* Contact Information */}
          <motion.div {...fadeInUp} data-testid="contact-info" className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-outfit font-bold tracking-tighter text-white mb-8">
                Let's Connect
              </h2>
              <p className="text-base text-gray-400 font-manrope leading-relaxed mb-8">
                Whether you're looking to start a new project, need a creative partner, or just want to say hello, we'd love to hear from you.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="bg-gradient-to-br from-[#7c3aed] to-[#3b82f6] p-3 rounded-2xl">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-manrope font-semibold mb-1">Email</h3>
                  <a
                    href="mailto:info@trivenzagroup.com"
                    data-testid="contact-email"
                    className="text-gray-400 hover:text-white font-manrope transition-colors duration-200"
                  >
                    info@trivenzagroup.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-gradient-to-br from-[#3b82f6] to-[#7c3aed] p-3 rounded-2xl">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-manrope font-semibold mb-1">Phone</h3>
                  <a
                    href="tel:6287652887"
                    data-testid="contact-phone"
                    className="text-gray-400 hover:text-white font-manrope transition-colors duration-200"
                  >
                    +91 6287652887
                  </a>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-outfit font-semibold text-white mb-4">Business Inquiries</h3>
              <p className="text-gray-400 font-manrope text-sm leading-relaxed">
                For business partnerships, client projects, and collaborations, please reach out using the contact form or email us directly.
              </p>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div {...fadeInUp} transition={{ delay: 0.2 }}>
            <form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              className="bg-[#0f172a]/50 backdrop-blur-sm border border-white/10 rounded-3xl p-8"
            >
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-white font-manrope font-medium mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    data-testid="contact-name-input"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#1e293b] border border-white/10 rounded-2xl px-4 py-3 text-white font-manrope focus:outline-none focus:border-[#7c3aed] transition-colors duration-200"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-white font-manrope font-medium mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    data-testid="contact-email-input"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#1e293b] border border-white/10 rounded-2xl px-4 py-3 text-white font-manrope focus:outline-none focus:border-[#7c3aed] transition-colors duration-200"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-white font-manrope font-medium mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    data-testid="contact-phone-input"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-[#1e293b] border border-white/10 rounded-2xl px-4 py-3 text-white font-manrope focus:outline-none focus:border-[#7c3aed] transition-colors duration-200"
                    placeholder="Your phone number"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-white font-manrope font-medium mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    data-testid="contact-message-input"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full bg-[#1e293b] border border-white/10 rounded-2xl px-4 py-3 text-white font-manrope focus:outline-none focus:border-[#7c3aed] transition-colors duration-200 resize-none"
                    placeholder="Tell us about your project..."
                  />
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={isSubmitting}
                  className="w-full gradient-background text-white font-manrope font-semibold px-8 py-4 rounded-full hover:scale-105 transition-transform duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send className="w-5 h-5" />
                </button>

                {submitStatus === 'success' && (
                  <div data-testid="contact-success-message" className="bg-green-500/20 border border-green-500/50 rounded-2xl p-4 text-green-400 font-manrope text-center">
                    Thank you! We'll get back to you soon.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div data-testid="contact-error-message" className="bg-red-500/20 border border-red-500/50 rounded-2xl p-4 text-red-400 font-manrope text-center">
                    Something went wrong. Please try again.
                  </div>
                )}
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#0f172a]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            {...fadeInUp}
            className="text-3xl md:text-4xl font-outfit font-bold tracking-tighter text-white mb-4"
          >
            TriVenza Private Limited
          </motion.h2>
          <motion.p {...fadeInUp} className="text-gray-400 font-manrope">
            Media Production & Digital Services Company
          </motion.p>
        </div>
      </section>
    </div>
  );
};

export default Contact;
